import { useEffect, useId, useState } from "react";
import {
  BookOpen,
  Bookmark,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Code,
  Eye,
  FileText,
  Flag,
  HardHat,
  Layers,
  Link2,
  List,
  Loader2,
  MessageSquare,
  OctagonAlert,
  Search,
  ShieldCheck,
  Table,
  Tags,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import {
  describeStep,
  shownSteps,
  stripCiteMarkers,
  summariseSteps,
  trailFromSteps,
  type AnswerStep,
  type AnswerTrail,
  type AskStage,
  type ChatSource,
} from "@gb/contracts";
import { DISPLAY_LOCALE } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AnswerBody } from "./answer-body";
import { StepDetailView } from "./step-detail";

/**
 * How the answer was made: one collapsed line that opens to one sentence per
 * step, each with a chevron to its detail (2026-09-26, Phase 5; §3 of
 * `plans/phase-5-answering-design.md`, `document-ai/0021` §12.4). The shape
 * is Claude Code's desktop app's.
 *
 * **Code writes every line**, with the `@gb/contracts` functions and the
 * reader's locale (`describeStep`, `summariseSteps`). The words are British
 * English; numbers and dates are the reader's; titles and terms are as
 * written; a note is the model's own words. No line here comes from a title:
 * the icon comes from the step's `tool`, and "looked at 1 drawing" from a
 * stored kind.
 *
 * **Open while the answer runs, folded for a past question** (the owner,
 * 2026-09-27; it was "collapsed by default, while the answer runs and after").
 * While an answer runs the list is open and grows: each step stays a line when
 * the next arrives, so nothing appears only to hide again. It stays open when
 * the answer finishes, for the reader who watched it; they may fold it. A
 * question opened later, finished, starts folded to the one summary line, and
 * one click opens it. A question still being answered when it is opened (from
 * History, a reload, a link) is running, so its list is open.
 *
 * The summary line is the only part that is `aria-live`, open or folded: a
 * reader using a screen reader hears the newest step, not every row.
 *
 * The block holds the stage line the screen used to show on its own, so there
 * is one line saying what the Brain is doing, not two.
 */

/** "Found in step 4": open the list, open that step, scroll to it and mark it. */
export type StepFocus = { seq: number; nonce: number };

/** How long a found step stays marked. */
const FOCUS_MARK_MS = 2_000;

const TOOL_ICONS: Record<string, LucideIcon> = {
  list_files: List,
  search: Search,
  open_file: FileText,
  versions: Layers,
  links: Link2,
  metadata: Tags,
  crew: HardHat,
  findings: ClipboardList,
  kept_looks: Bookmark,
  look: Eye,
  rows_for_code: Table,
  code_execution: Code,
};

function iconFor(step: AnswerStep): LucideIcon {
  switch (step.kind) {
    case "context":
      return BookOpen;
    case "note":
      return MessageSquare;
    case "limit":
      return OctagonAlert;
    case "wrap-up":
      return Flag;
    case "review":
      return ShieldCheck;
    default:
      // A tool this build does not know still gets a row, with a plain icon.
      return (step.tool && TOOL_ICONS[step.tool]) || Wrench;
  }
}

function num(n: number): string {
  return new Intl.NumberFormat(DISPLAY_LOCALE).format(n);
}

/** Tool calls: what "· 7 steps" counts. Kept-look reads and code runs are calls too. */
function toolSteps(steps: readonly AnswerStep[]): AnswerStep[] {
  return steps.filter((step) => step.kind === "tool");
}

/**
 * The collapsed line.
 *
 * While running: "Checking the answer…" during the review; "Writing the
 * answer…" while the answer is written; else the newest running step's
 * sentence; else the stage's own label; else "Thinking…". The count of steps
 * so far goes on the right.
 *
 * When finished: the counts by tool, the time and how it stopped
 * (`summariseSteps`), in the warning tone when it stopped early.
 */
export function collapsedLine(input: {
  running: boolean;
  stage: AskStage | null;
  /** The screen's label for the stage, for the stages this block has no line of its own for. */
  stageLabel: string | null;
  steps: readonly AnswerStep[];
  trail: AnswerTrail | null;
  /** The stream ended in an error, with no trail: the summary says it stopped early. */
  failed?: boolean;
}): { text: string; warn: boolean; count: string | null; spinning: boolean } {
  const calls = toolSteps(input.steps);
  const count = calls.length > 0 ? `${num(calls.length)} ${calls.length === 1 ? "step" : "steps"}` : null;
  if (input.running) {
    const newest = [...calls].reverse().find((step) => step.outcome === "running");
    const text =
      input.stage === "checking"
        ? "Checking the answer…"
        : input.stage === "answering"
          ? "Writing the answer…"
          : newest
            ? describeStep(newest, DISPLAY_LOCALE)
            : calls.length === 0 && input.stageLabel
              ? input.stageLabel
              : "Thinking…";
    return { text, warn: false, count, spinning: true };
  }
  const trail = input.trail ?? trailOf(input.steps, input.failed ? "error" : "answer");
  return {
    text: summariseSteps(trail, DISPLAY_LOCALE),
    warn: trail.endedBy !== "answer",
    count: null,
    spinning: false,
  };
}

/** The counts of the steps, when there is no stored trail. From the first start to the last finish. */
function trailOf(steps: readonly AnswerStep[], endedBy: "answer" | "error"): AnswerTrail {
  const starts = steps.map((step) => Date.parse(step.startedAt)).filter(Number.isFinite);
  const ends = steps.map((step) => Date.parse(step.finishedAt ?? "")).filter(Number.isFinite);
  const elapsedMs = starts.length > 0 && ends.length > 0 ? Math.max(...ends) - Math.min(...starts) : 0;
  return trailFromSteps(steps, { elapsedMs, endedBy });
}

/**
 * The rows, in order. A model turn is machinery, not a step, and is left out;
 * so is a note the server kept but does not show (the one that stood in for
 * unfinished work, §1.2).
 *
 * Every note here is a step the server sent (2026-09-27). A turn's streamed
 * text is a note only once the server's text note of that turn arrives
 * (`streamedAnswer`), so no note is still streaming when its turn's steps
 * are listed.
 */
export function stepRows(steps: readonly AnswerStep[]): AnswerStep[] {
  return shownSteps(steps);
}

export function stepAnchorId(seq: number): string {
  return `answer-step-${seq}`;
}

export function AnswerSteps({
  projectId,
  steps,
  trail,
  running,
  stage,
  stageLabel,
  failed = false,
  sources,
  onCite,
  focus,
}: {
  projectId: string;
  steps: readonly AnswerStep[];
  /** The stored or streamed counts of a finished answer. */
  trail: AnswerTrail | null;
  running: boolean;
  stage: AskStage | null;
  stageLabel: string | null;
  /** The stream ended in an error before a trail was sent. */
  failed?: boolean;
  /** The answer's sources, for a note's citation chips. */
  sources: readonly ChatSource[];
  onCite: (n: number) => void;
  focus: StepFocus | null;
}) {
  // Open when it first shows a running answer, folded when it first shows a
  // finished one. The screen keys this block by question, so a new question
  // starts again from here.
  const [open, setOpen] = useState(running);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [marked, setMarked] = useState<number | null>(null);
  const listId = useId();

  // An answer that starts running under this block opens it. Only on the
  // change: a reader who folds the list while it runs keeps it folded, and
  // the answer finishing leaves it as the reader has it.
  useEffect(() => {
    if (running) setOpen(true);
  }, [running]);

  // "Found in step N" on a source card.
  useEffect(() => {
    if (!focus) return;
    setOpen(true);
    setExpanded((now) => new Set(now).add(focus.seq));
    setMarked(focus.seq);
    const scroll = window.setTimeout(() => {
      document.getElementById(stepAnchorId(focus.seq))?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 0);
    const unmark = window.setTimeout(() => setMarked(null), FOCUS_MARK_MS);
    return () => {
      window.clearTimeout(scroll);
      window.clearTimeout(unmark);
    };
  }, [focus]);

  const line = collapsedLine({ running, stage, stageLabel, steps, trail, failed });
  const rows = stepRows(steps);

  const toggle = (seq: number) =>
    setExpanded((now) => {
      const next = new Set(now);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });

  return (
    <div className="rounded-card border-[1.5px] border-border bg-surface-sunken/60">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((now) => !now)}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown size={14} className="shrink-0 text-muted" aria-hidden />
        ) : (
          <ChevronRight size={14} className="shrink-0 text-muted" aria-hidden />
        )}
        {line.spinning && <Loader2 size={13} className="shrink-0 animate-spin text-muted" aria-hidden />}
        <span
          aria-live="polite"
          data-testid="steps-line"
          className={cn("min-w-0 flex-1 truncate text-[0.83rem]", line.warn ? "font-semibold text-warn" : "text-ink-slate")}
        >
          {line.text}
        </span>
        {line.count && <span className="shrink-0 text-[0.75rem] text-muted">· {line.count}</span>}
      </button>

      {/* No empty list while the first step is awaited: the summary line
          already says "Thinking…", and a "No steps yet." row would appear
          only to be replaced by the first step. */}
      {open && (rows.length > 0 || !running) && (
        <ol id={listId} className="flex flex-col border-t border-border px-2 py-1.5">
          {rows.length === 0 && <li className="px-2 py-1.5 text-[0.8rem] text-muted">No steps yet.</li>}
          {rows.map((step) => (
            <StepRowView
              key={step.seq}
              step={step}
              projectId={projectId}
              sources={sources}
              onCite={onCite}
              expanded={expanded.has(step.seq)}
              marked={marked === step.seq}
              onToggle={() => toggle(step.seq)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function StepRowView({
  step,
  projectId,
  sources,
  onCite,
  expanded,
  marked,
  onToggle,
}: {
  step: AnswerStep;
  projectId: string;
  sources: readonly ChatSource[];
  onCite: (n: number) => void;
  expanded: boolean;
  marked: boolean;
  onToggle: () => void;
}) {
  const Icon = iconFor(step);

  if (step.kind === "note") {
    // A note is the model's own words between steps. Only a `text`-block note
    // can cite, so only it gets chips; a thinking block's is the API's
    // summary, whose markers the server already removed (§1.8).
    // "Cited in the note at step N" finds it by its anchor, as a step's row.
    const text = step.note?.text ?? "";
    return (
      <li
        id={stepAnchorId(step.seq)}
        data-marked={marked || undefined}
        className={cn(
          "flex items-start gap-2 rounded-input px-2 py-1 text-[0.8rem] text-muted italic transition-colors",
          marked && "bg-brand/10 ring-1 ring-brand/40",
        )}
      >
        <Icon size={13} className="mt-[3px] shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 [&_p]:m-0">
          {step.note?.form === "text" ? (
            <AnswerBody text={text} sources={sources} onCite={onCite} />
          ) : (
            stripCiteMarkers(text)
          )}
        </div>
      </li>
    );
  }

  const failed = step.outcome === "error" || step.outcome === "timeout" || step.kind === "limit";
  return (
    <li
      id={stepAnchorId(step.seq)}
      data-marked={marked || undefined}
      className={cn("rounded-input transition-colors", marked && "bg-brand/10 ring-1 ring-brand/40")}
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex w-full cursor-pointer items-start gap-2 px-2 py-1 text-left text-[0.82rem] hover:bg-surface"
      >
        <span className="w-[1.4rem] shrink-0 pt-[1px] text-right font-mono text-[0.7rem] text-muted">{step.seq}</span>
        {step.outcome === "running" ? (
          <Loader2 size={13} className="mt-[3px] shrink-0 animate-spin text-muted" aria-label="Running" />
        ) : (
          <Icon size={13} className={cn("mt-[3px] shrink-0", failed ? "text-warn" : "text-muted")} aria-hidden />
        )}
        <span className={cn("min-w-0 flex-1", failed ? "text-warn" : "text-ink")}>{describeStep(step, DISPLAY_LOCALE)}</span>
        {expanded ? (
          <ChevronDown size={13} className="mt-[3px] shrink-0 text-muted" aria-hidden />
        ) : (
          <ChevronRight size={13} className="mt-[3px] shrink-0 text-muted" aria-hidden />
        )}
      </button>
      {expanded && (
        <div className="pt-0.5 pr-2 pb-2 pl-[3.6rem]">
          <StepDetailView step={step} projectId={projectId} onCite={onCite} />
        </div>
      )}
    </li>
  );
}
